import axios from 'axios';

const API_URL = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
const INPUT_TYPE = "JSON";
const RESPONSE_TYPE = "JSON";

/**
 * Master Data Service for CRR_MasterData module
 * Provides functions to fetch, add, edit, and delete master data
 * Returns castes, religions, and relationships separately
 */

// Helper function to process master data entries
const processMasterData = (entryList) => {
  const result = entryList.map((entry) => ({
    id: entry.id,
    ...Object.fromEntries(
      Object.values(entry.name_value_list).map((field) => [field.name, field.value])
    ),
  }));

  return {
    castes: result
      .filter((item) => item.category_c === "Caste")
      .sort((a, b) => a.name.localeCompare(b.name)),
    religions: result
      .filter((item) => item.category_c === "Religion")
      .sort((a, b) => a.name.localeCompare(b.name)),
    relationships: result
      .filter((item) => item.category_c === "Relationship")
      .sort((a, b) => a.name.localeCompare(b.name)),
  };
};

/**
 * Fetch all master data from CRR_MasterData module
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Object>} - Object containing castes, religions, and relationships arrays
 */
export const fetchMasterData = async (sessionId) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "CRR_MasterData",
    });

    const fullUrl = `${API_URL}?method=get_entry_list&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.get(fullUrl);
    const entryList = response.data.entry_list;

    return processMasterData(entryList);
  } catch (error) {
    console.error('Error fetching master data:', error);
    throw new Error('Failed to fetch master data');
  }
};

/**
 * Add new master data entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {Object} data - Master data object with name, category_c, and other fields
 * @returns {Promise<Object>} - Created entry data
 */
export const addMasterData = async (sessionId, data) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "CRR_MasterData",
      name_value_list: Object.entries(data).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);
    return response.data;
  } catch (error) {
    console.error('Error adding master data:', error);
    throw new Error('Failed to add master data');
  }
};

/**
 * Edit existing master data entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to update
 * @param {Object} data - Updated master data object
 * @returns {Promise<Object>} - Updated entry data
 */
export const editMasterData = async (sessionId, id, data) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "CRR_MasterData",
      id: id,
      name_value_list: Object.entries(data).map(([key, value]) => ({
        name: key,
        value: value
      }))
    });

    const fullUrl = `${API_URL}?method=set_entry&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);
    return response.data;
  } catch (error) {
    console.error('Error editing master data:', error);
    throw new Error('Failed to edit master data');
  }
};

/**
 * Delete master data entry
 * @param {string} sessionId - SuiteCRM session ID
 * @param {string} id - Entry ID to delete
 * @returns {Promise<Object>} - Deletion result
 */
export const deleteMasterData = async (sessionId, id) => {
  try {
    const rest_data = JSON.stringify({
      session: sessionId,
      module_name: "CRR_MasterData",
      id: id
    });

    const fullUrl = `${API_URL}?method=set_deleted&input_type=${INPUT_TYPE}&response_type=${RESPONSE_TYPE}&rest_data=${encodeURIComponent(rest_data)}`;

    const response = await axios.post(fullUrl);
    return response.data;
  } catch (error) {
    console.error('Error deleting master data:', error);
    throw new Error('Failed to delete master data');
  }
};

/**
 * Get castes from master data
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of caste objects
 */
export const getCastes = async (sessionId) => {
  try {
    const data = await fetchMasterData(sessionId);
    return data.castes;
  } catch (error) {
    console.error('Error fetching castes:', error);
    return [];
  }
};

/**
 * Get religions from master data
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of religion objects
 */
export const getReligions = async (sessionId) => {
  try {
    const data = await fetchMasterData(sessionId);
    return data.religions;
  } catch (error) {
    console.error('Error fetching religions:', error);
    return [];
  }
};

/**
 * Get relationships from master data
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of relationship objects
 */
export const getRelationships = async (sessionId) => {
  try {
    const data = await fetchMasterData(sessionId);
    return data.relationships;
  } catch (error) {
    console.error('Error fetching relationships:', error);
    return [];
  }
};

/**
 * Get caste names only (for dropdowns)
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of caste names
 */
export const getCasteNames = async (sessionId) => {
  try {
    const castes = await getCastes(sessionId);
    return castes.map(item => item.name);
  } catch (error) {
    console.error('Error fetching caste names:', error);
    return [];
  }
};

/**
 * Get religion names only (for dropdowns)
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of religion names
 */
export const getReligionNames = async (sessionId) => {
  try {
    const religions = await getReligions(sessionId);
    return religions.map(item => item.name);
  } catch (error) {
    console.error('Error fetching religion names:', error);
    return [];
  }
};

/**
 * Get relationship names only (for dropdowns)
 * @param {string} sessionId - SuiteCRM session ID
 * @returns {Promise<Array>} - Array of relationship names
 */
export const getRelationshipNames = async (sessionId) => {
  try {
    const relationships = await getRelationships(sessionId);
    return relationships.map(item => item.name);
  } catch (error) {
    console.error('Error fetching relationship names:', error);
    return [];
  }
};
